import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FileEdit, Lock, StickyNote } from "lucide-react";
import { format } from "date-fns";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { getOptionLabel, REPORT_CATEGORIES } from "@/lib/reportOptionsData";

interface ReportNote {
  id: string;
  note: string;
  created_at: string;
  created_by?: string | null;
  created_by_name?: string | null;
}

interface EditReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: string;
  /** Which report table this report lives in */
  table?: "lead_reports" | "booking_reports";
  leadId?: string;
  bookingId?: string;
  title?: string | null;
}

const RATING_FIELDS: { categoryId: string; field: string }[] = [
  { categoryId: "timing", field: "timing_rating" },
  { categoryId: "engagement", field: "engagement_rating" },
  { categoryId: "transport", field: "transport_rating" },
  { categoryId: "satisfaction", field: "satisfaction_rating" },
  { categoryId: "guide_rating", field: "guide_rating" },
  { categoryId: "issues", field: "issues_status" },
];

const getCategoryLabel = (categoryId: string): string => {
  const category = REPORT_CATEGORIES.find((c) => c.id === categoryId);
  return category?.name || categoryId;
};

/**
 * Universal report addendum editor.
 * The originally submitted data is always shown read-only (locked);
 * staff can only append timestamped additional notes.
 */
export function EditReportDialog({
  open,
  onOpenChange,
  reportId,
  table = "lead_reports",
  leadId,
  bookingId,
  title,
}: EditReportDialogProps) {
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState("");

  const { data: report, isLoading } = useQuery({
    queryKey: ["report-edit", table, reportId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(table)
        .select("*")
        .eq("id", reportId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: open && !!reportId,
  });

  useEffect(() => {
    if (open) setNewNote("");
  }, [open, reportId]);

  const notes: ReportNote[] = Array.isArray(report?.report_notes)
    ? (report.report_notes as unknown as ReportNote[])
    : [];

  const addNoteMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", user.id)
        .maybeSingle();

      const note: ReportNote = {
        id: crypto.randomUUID(),
        note: newNote.trim(),
        created_at: new Date().toISOString(),
        created_by: user.id,
        created_by_name: profile?.full_name || profile?.email || user.email || null,
      };

      const { error: updateError } = await supabase
        .from(table)
        .update({ report_notes: [...notes, note] as any })
        .eq("id", reportId);

      if (updateError) throw updateError;

      // Audit trail
      if (table === "lead_reports" && leadId) {
        await supabase.from("activity_log").insert({
          lead_id: leadId,
          user_id: user.id,
          action: "report_note_added",
          details: "An additional note was added to the activity report",
        });
      } else if (table === "booking_reports" && bookingId) {
        await supabase.from("coffeebeans_booking_history").insert({
          booking_id: bookingId,
          changed_by: user.id,
          action: "report_note_added",
          notes: "An additional note was added to the booking report",
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-edit", table, reportId] });
      if (table === "lead_reports") {
        queryClient.invalidateQueries({ queryKey: ["lead-report", leadId] });
        queryClient.invalidateQueries({ queryKey: ["lead-component-reports", leadId] });
        queryClient.invalidateQueries({ queryKey: ["lead-component-report-view"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["booking-report", bookingId] });
      }

      toast({
        title: "Note Added",
        description: "Your note has been added to the report. The original submission is unchanged.",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Error adding report note:", error);
      toast({
        title: "Error",
        description: "Failed to add the note. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileEdit className="h-5 w-5" />
            Add Note to Report
          </DialogTitle>
          <DialogDescription>
            {title || "Report addendum"}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : !report ? (
          <div className="text-center py-8 text-muted-foreground">Report not found.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto pr-4">
            <div className="space-y-6">
              {/* Locked original submission */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Original submission
                  <span className="text-xs font-normal text-muted-foreground">
                    (locked — cannot be changed)
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {RATING_FIELDS.map(({ categoryId, field }) => {
                    const value = (report as any)[field] as string | null;
                    return (
                      <div key={field} className="space-y-1">
                        <p className="text-xs text-muted-foreground">{getCategoryLabel(categoryId)}</p>
                        {value ? (
                          value === "n/a" ? (
                            <Badge variant="secondary">N/A</Badge>
                          ) : (
                            <Badge variant="outline">{getOptionLabel(categoryId, value)}</Badge>
                          )
                        ) : (
                          <span className="text-muted-foreground text-sm">Not rated</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {report.client_feedback && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Client Feedback</p>
                    <p className="text-sm italic">"{report.client_feedback}"</p>
                  </div>
                )}

                {report.guide_feedback && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Guide Feedback</p>
                    <p className="text-sm whitespace-pre-wrap">{report.guide_feedback}</p>
                  </div>
                )}

                {report.additional_notes && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Additional Notes</p>
                    <p className="text-sm whitespace-pre-wrap">{report.additional_notes}</p>
                  </div>
                )}
              </div>

              {/* Existing addendum notes */}
              {notes.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <StickyNote className="h-4 w-4" />
                      Notes added after submission
                    </p>
                    {notes.map((n) => (
                      <div key={n.id} className="rounded-lg border p-3 space-y-1">
                        <p className="text-sm whitespace-pre-wrap">{n.note}</p>
                        <p className="text-xs text-muted-foreground">
                          {n.created_by_name || "Team member"}
                          {n.created_at && ` • ${format(new Date(n.created_at), "MMM dd, yyyy 'at' HH:mm")}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* New note */}
              <div className="space-y-2">
                <Label htmlFor="report_new_note">Add a note</Label>
                <p className="text-xs text-muted-foreground">
                  Notes are appended to the report with your name and the date. The original submission stays intact.
                </p>
                <Textarea
                  id="report_new_note"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Follow-up context, corrections, or post-tour developments..."
                  className="min-h-[96px] max-h-[45vh] resize-y overflow-y-auto"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => addNoteMutation.mutate()}
            disabled={!newNote.trim() || addNoteMutation.isPending || isLoading || !report}
          >
            {addNoteMutation.isPending ? "Saving..." : "Add Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
